import React, { useState } from 'react';

import Grid from '@mui/material/Grid2';
import {
  Card,
  Box,
  Typography,
  Button,
  TextField,
  Stack,
  Alert,
  Chip,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { TextEditedContent } from 'components';
import PropTypes from 'prop-types';
import api from 'utils/api';
import { DOLPHIN_API } from 'shared/constants/apis';
import { AdminModal } from '../components/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useSnackbar } from 'notistack';

const propTypes = {
  close: PropTypes.func,
  fetchUsers: PropTypes.func,
};

const AddNewUser = ({ close, fetchUsers }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [formState, setFormState] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    password: '',
    userType: '',
    preferredLanguage: 'ja-JP',
    addNoMfaAttribute: false,
  });
  const [incompleteFields, setIncompleteFields] = useState([]);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [incompleteWarningModalOpen, setIncompleteWarningModalOpen] =
    useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const keyToLabel = {
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'E-mail',
    username: 'Username',
    password: 'Password',
    userType: 'User Type',
    preferredLanguage: 'Preferred Language',
  };

  const validatePassword = (password) => {
    const errors = [];

    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }

    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }

    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }

    if (!/[0-9]/.test(password)) {
      errors.push('Password must contain at least one number');
    }

    return errors;
  };

  const onValueChange = (key, value) => {
    setFormState((prevState) => ({
      ...prevState,
      [key]: value,
    }));

    // Validate password in real-time
    if (key === 'password') {
      const errors = validatePassword(value);
      setPasswordErrors(errors);
    }
  };

  const sendToServer = async () => {
    setIsLoading(true);
    try {
      const data = { ...formState };

      // Add MFA attribute if selected
      if (formState.addNoMfaAttribute) {
        data.userAttributes = {
          'custom:preferredMfaType': 'NOMFA',
        };
      }

      // Remove the checkbox field from the data sent to server
      delete data.addNoMfaAttribute;

      const response = await api.post(DOLPHIN_API.ADMIN_USERS, {
        data: {
          createdData: [data],
        },
      });

      setResponseData(response);
      setShowResults(true);
      await fetchUsers();
      enqueueSnackbar('User created successfully', {
        variant: 'success',
      });
    } catch (error) {
      console.error('Error creating user:', error);
      enqueueSnackbar('Error creating user:', {
        variant: 'error',
      });
      setResponseData({
        message: 'Failed to create user',
        summary: {
          totalUsers: 1,
          successfulDbCreations: 0,
          successfulOpenIMRegistrations: 0,
          successfulCognitoCreations: 0,
          fullySuccessful: 0,
        },
        results: [
          {
            userData: formState,
            dbCreation: {
              success: false,
              error: { message: error.message },
              user: null,
            },
            openIMRegistration: {
              success: false,
              error: { message: error.message },
              response: null,
            },
            cognitoUserCreation: {
              success: false,
              error: { message: error.message },
              response: null,
            },
          },
        ],
      });
      setShowResults(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setConfirmModalOpen(false);
    setShowResults(false);
    setResponseData(null);
    close();
  };

  const checkIfFormCompleted = () => {
    const _incompleteFields = [];

    // Check required fields (excluding addNoMfaAttribute which is optional)
    const requiredFields = [
      'firstName',
      'lastName',
      'email',
      'username',
      'password',
      'userType',
      'preferredLanguage',
    ];

    for (const key of requiredFields) {
      const value = formState[key];
      if (!value || value.length === 0) {
        _incompleteFields.push(keyToLabel[key]);
      }
    }

    // Check password validation
    const passwordValidationErrors = validatePassword(formState.password);

    if (_incompleteFields.length > 0 || passwordValidationErrors.length > 0) {
      setIncompleteFields(_incompleteFields);
      setPasswordErrors(passwordValidationErrors);
      return false;
    } else {
      return true;
    }
  };

  const handleSubmitClick = async (event) => {
    event.preventDefault();
    if (!checkIfFormCompleted()) {
      setIncompleteWarningModalOpen(true);
    } else {
      setConfirmModalOpen(true);
    }
  };

  const renderResultsContent = () => {
    if (!responseData) {
      return (
        <Box>
          <Typography variant="h6" mb={1}>
            Error: No Response Data
          </Typography>
          <Alert severity="error" sx={{ mb: 1, py: 1 }}>
            No response data received from server.
          </Alert>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleCloseModal}
            style={{ color: 'white' }}>
            Close
          </Button>
        </Box>
      );
    }

    const { message, summary, results } = responseData;

    if (!results || results.length === 0) {
      return (
        <Box>
          <Typography variant="h6" mb={1}>
            Error: No Results
          </Typography>
          <Alert severity="error" sx={{ mb: 1, py: 1 }}>
            No results found in server response.
          </Alert>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleCloseModal}
            style={{ color: 'white' }}>
            Close
          </Button>
        </Box>
      );
    }

    const result = results[0]; // Since we're creating one user at a time

    return (
      <Box>
        <Typography variant="h6" mb={1}>
          User Creation Results
        </Typography>

        {/* Summary Alert */}
        <Alert
          severity={summary.fullySuccessful > 0 ? 'success' : 'error'}
          icon={
            summary.fullySuccessful > 0 ? <CheckCircleIcon /> : <ErrorIcon />
          }
          sx={{ mb: 1, py: 1 }}>
          <Typography variant="body2">{message}</Typography>
        </Alert>

        {/* Summary Statistics */}
        <Box mb={1}>
          <Typography variant="subtitle2" fontWeight="bold" mb={1}>
            Summary:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              label={`Database Creation: ${summary.successfulDbCreations}/${summary.totalUsers}`}
              color={summary.successfulDbCreations > 0 ? 'success' : 'error'}
              variant="outlined"
              size="small"
            />
            <Chip
              label={`OpenIM Registration: ${summary.successfulOpenIMRegistrations}/${summary.totalUsers}`}
              color={
                summary.successfulOpenIMRegistrations > 0 ? 'success' : 'error'
              }
              variant="outlined"
              size="small"
            />
            <Chip
              label={`Cognito Creation: ${summary.successfulCognitoCreations}/${summary.totalUsers}`}
              color={
                summary.successfulCognitoCreations > 0 ? 'success' : 'error'
              }
              variant="outlined"
              size="small"
            />
            <Chip
              label={`Fully Successful: ${summary.fullySuccessful}/${summary.totalUsers}`}
              color={summary.fullySuccessful > 0 ? 'success' : 'error'}
              variant="outlined"
              size="small"
            />
          </Stack>
        </Box>

        <Divider sx={{ mb: 1 }} />

        {/* Detailed Results */}
        <Box>
          <Typography variant="h6" fontWeight="bold" mb={1}>
            Detailed Results:
          </Typography>

          {/* First Row: Database and Cognito Results */}
          <Grid container spacing={2} mb={2}>
            {/* Database Creation Result */}
            <Grid xs={6}>
              <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <Typography variant="body2" fontWeight="bold">
                    Database Creation:
                  </Typography>
                  <Chip
                    size="small"
                    label={result.dbCreation.success ? 'Success' : 'Failed'}
                    color={result.dbCreation.success ? 'success' : 'error'}
                    icon={
                      result.dbCreation.success ? (
                        <CheckCircleIcon />
                      ) : (
                        <ErrorIcon />
                      )
                    }
                  />
                </Stack>

                {result.dbCreation.success && result.dbCreation.user ? (
                  <Box
                    sx={{
                      bgcolor: 'success.light',
                      p: 1.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'success.main',
                      mb: 1,
                      maxWidth: '100%',
                      overflow: 'hidden',
                    }}>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      mb={0.5}
                      color="success.dark">
                      Created User Object:
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: 'background.paper',
                        p: 1,
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        maxHeight: '300px',
                        overflow: 'auto',
                        wordBreak: 'break-all',
                        overflowWrap: 'break-word',
                      }}>
                      <pre
                        style={{
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                          overflowWrap: 'break-word',
                        }}>
                        {JSON.stringify(result.dbCreation.user, null, 2)}
                      </pre>
                    </Box>
                  </Box>
                ) : (
                  result.dbCreation.error && (
                    <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
                      <Typography variant="body2" fontWeight="bold">
                        Database Creation Failed:
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {result.dbCreation.error.message}
                      </Typography>
                      {result.dbCreation.error.details && (
                        <Box
                          sx={{
                            bgcolor: 'error.light',
                            p: 0.5,
                            borderRadius: 1,
                            mt: 0.5,
                            fontFamily: 'monospace',
                            fontSize: '0.7rem',
                            wordBreak: 'break-all',
                            overflowWrap: 'break-word',
                          }}>
                          <pre
                            style={{
                              margin: 0,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-all',
                              overflowWrap: 'break-word',
                            }}>
                            {JSON.stringify(
                              result.dbCreation.error.details,
                              null,
                              2
                            )}
                          </pre>
                        </Box>
                      )}
                    </Alert>
                  )
                )}
              </Box>
            </Grid>

            {/* Cognito User Creation Result */}
            <Grid xs={6}>
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <Typography variant="body2" fontWeight="bold">
                    Cognito User Creation:
                  </Typography>
                  <Chip
                    size="small"
                    label={
                      result.cognitoUserCreation.success ? 'Success' : 'Failed'
                    }
                    color={
                      result.cognitoUserCreation.success ? 'success' : 'error'
                    }
                    icon={
                      result.cognitoUserCreation.success ? (
                        <CheckCircleIcon />
                      ) : (
                        <ErrorIcon />
                      )
                    }
                  />
                </Stack>

                {result.cognitoUserCreation.success &&
                result.cognitoUserCreation.response ? (
                  <Box
                    sx={{
                      bgcolor: 'success.light',
                      p: 1.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'success.main',
                      mb: 1,
                      maxWidth: '100%',
                      overflow: 'hidden',
                    }}>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      mb={0.5}
                      color="success.dark">
                      Cognito User Creation Response:
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: 'background.paper',
                        p: 1,
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        maxHeight: '300px',
                        overflow: 'auto',
                        wordBreak: 'break-all',
                        overflowWrap: 'break-word',
                      }}>
                      <pre
                        style={{
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                          overflowWrap: 'break-word',
                        }}>
                        {JSON.stringify(
                          result.cognitoUserCreation.response,
                          null,
                          2
                        )}
                      </pre>
                    </Box>
                  </Box>
                ) : (
                  result.cognitoUserCreation.error && (
                    <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
                      <Typography variant="body2" fontWeight="bold">
                        Cognito User Creation Failed:
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {result.cognitoUserCreation.error.message}
                      </Typography>
                      {result.cognitoUserCreation.error.code && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Error Code: {result.cognitoUserCreation.error.code}
                        </Typography>
                      )}
                      {result.cognitoUserCreation.error.details && (
                        <Box
                          sx={{
                            bgcolor: 'error.light',
                            p: 0.5,
                            borderRadius: 1,
                            mt: 0.5,
                            fontFamily: 'monospace',
                            fontSize: '0.7rem',
                            wordBreak: 'break-all',
                            overflowWrap: 'break-word',
                          }}>
                          <pre
                            style={{
                              margin: 0,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-all',
                              overflowWrap: 'break-word',
                            }}>
                            {JSON.stringify(
                              result.cognitoUserCreation.error.details,
                              null,
                              2
                            )}
                          </pre>
                        </Box>
                      )}
                    </Alert>
                  )
                )}
              </Box>
            </Grid>

            {/* Second Row: OpenIM Registration Result */}
            <Grid xs={12}>
              <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <Typography variant="body2" fontWeight="bold">
                    OpenIM Registration:
                  </Typography>
                  <Chip
                    size="small"
                    label={
                      result.openIMRegistration.success ? 'Success' : 'Failed'
                    }
                    color={
                      result.openIMRegistration.success ? 'success' : 'error'
                    }
                    icon={
                      result.openIMRegistration.success ? (
                        <CheckCircleIcon />
                      ) : (
                        <ErrorIcon />
                      )
                    }
                  />
                </Stack>

                {result.openIMRegistration.success &&
                result.openIMRegistration.response ? (
                  <Box
                    sx={{
                      bgcolor: 'success.light',
                      p: 1.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'success.main',
                      mb: 1,
                      maxWidth: '100%',
                      overflow: 'hidden',
                    }}>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      mb={0.5}
                      color="success.dark">
                      OpenIM Registration Response:
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: 'background.paper',
                        p: 1,
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        maxHeight: '300px',
                        overflow: 'auto',
                        wordBreak: 'break-all',
                        overflowWrap: 'break-word',
                      }}>
                      <pre
                        style={{
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                          overflowWrap: 'break-word',
                        }}>
                        {JSON.stringify(
                          result.openIMRegistration.response,
                          null,
                          2
                        )}
                      </pre>
                    </Box>
                  </Box>
                ) : (
                  result.openIMRegistration.error && (
                    <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
                      <Typography variant="body2" fontWeight="bold">
                        OpenIM Registration Failed:
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {result.openIMRegistration.error.message}
                      </Typography>
                      {result.openIMRegistration.error.status && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Status: {result.openIMRegistration.error.status}
                        </Typography>
                      )}
                      {result.openIMRegistration.error.response && (
                        <Box
                          sx={{
                            bgcolor: 'error.light',
                            p: 0.5,
                            borderRadius: 1,
                            mt: 0.5,
                            fontFamily: 'monospace',
                            fontSize: '0.7rem',
                            wordBreak: 'break-all',
                            overflowWrap: 'break-word',
                          }}>
                          <pre
                            style={{
                              margin: 0,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-all',
                              overflowWrap: 'break-word',
                            }}>
                            {JSON.stringify(
                              result.openIMRegistration.error.response,
                              null,
                              2
                            )}
                          </pre>
                        </Box>
                      )}
                    </Alert>
                  )
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box mt={2}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleCloseModal}
            style={{ color: 'white' }}>
            Close
          </Button>
        </Box>
      </Box>
    );
  };

  const renderConfirmContent = () => {
    // Prepare the data to show in confirmation (same as what will be sent to server)
    const confirmData = { ...formState };

    // Add MFA attribute if selected
    if (formState.addNoMfaAttribute) {
      confirmData.userAttributes = {
        'custom:preferredMfaType': 'NOMFA',
      };
    }

    // Remove the checkbox field from display
    delete confirmData.addNoMfaAttribute;

    return (
      <Box>
        <Typography variant="h6" mb={1}>
          Confirm
        </Typography>
        <Typography>You are going to create a new user as below:</Typography>
        <TextEditedContent
          content={JSON.stringify(confirmData, undefined, 4)}
        />
        <Grid spacing={3} container justifyContent="space-between">
          <Grid>
            <Button
              variant="contained"
              color="primary"
              style={{
                color: 'white',
              }}
              onClick={sendToServer}
              disabled={isLoading}>
              {isLoading ? 'Creating...' : 'Confirm'}
            </Button>
          </Grid>
          <Grid>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setIncompleteWarningModalOpen(false);
                setConfirmModalOpen(false);
              }}
              disabled={isLoading}>
              Cancel
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <>
      <Box m={7}>
        <Stack spacing={3}>
          <TextField
            id="firstName"
            name="firstName"
            fullWidth
            variant="outlined"
            label="First Name"
            onChange={(event) => onValueChange('firstName', event.target.value)}
            value={formState.firstName}
          />

          <TextField
            id="lastName"
            name="lastName"
            fullWidth
            variant="outlined"
            label="Last Name"
            onChange={(event) => onValueChange('lastName', event.target.value)}
            value={formState.lastName}
          />

          <TextField
            id="email"
            name="email"
            fullWidth
            variant="outlined"
            label="Email"
            onChange={(event) => onValueChange('email', event.target.value)}
            value={formState.email}
            color="secondary"
          />

          <TextField
            id="username"
            name="username"
            fullWidth
            variant="outlined"
            label="Username"
            onChange={(event) => onValueChange('username', event.target.value)}
            value={formState.username}
            color="secondary"
          />

          <TextField
            id="password"
            name="password"
            fullWidth
            variant="outlined"
            label="Password"
            type="text"
            onChange={(event) => onValueChange('password', event.target.value)}
            value={formState.password}
            color="secondary"
            error={passwordErrors.length > 0}
            helperText={passwordErrors.join(', ')}
          />

          <Autocomplete
            disablePortal
            id="userType"
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            onChange={(event, newValue) => {
              if (newValue && newValue.value)
                onValueChange('userType', newValue.value);
            }}
            options={[
              { label: 'Staff', value: 'staff' },
              { label: 'Doctor', value: 'doctor' },
              { label: 'Manager', value: 'manager' },
            ]}
            renderInput={(params) => {
              return (
                <TextField
                  {...params}
                  name="userType"
                  fullWidth
                  variant="outlined"
                  label="User Type"
                  color="secondary"
                />
              );
            }}
          />

          <Autocomplete
            disablePortal
            id="preferredLanguage"
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            onChange={(event, newValue) => {
              if (newValue && newValue.value)
                onValueChange('preferredLanguage', newValue.value);
            }}
            options={[
              { label: 'Chinese', value: 'zh-CN' },
              { label: 'English', value: 'en-US' },
              { label: 'Japanese', value: 'ja-JP' },
            ]}
            renderInput={(params) => {
              return (
                <TextField
                  {...params}
                  name="preferredLanguage"
                  fullWidth
                  variant="outlined"
                  label="Preferred Language"
                  color="secondary"
                />
              );
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={formState.addNoMfaAttribute}
                onChange={(event) =>
                  onValueChange('addNoMfaAttribute', event.target.checked)
                }
                name="addNoMfaAttribute"
                color="secondary"
              />
            }
            label="Add custom:preferredMfaType as NOMFA attribute"
          />

          <Grid spacing={3} container justifyContent="space-between">
            <Grid>
              <Button
                variant="contained"
                color="primary"
                style={{
                  color: 'white',
                  width: '50%',
                  marginLeft: '25%',
                }}
                onClick={handleSubmitClick}>
                Submit
              </Button>
            </Grid>
            <Grid>
              <Button
                variant="contained"
                color="secondary"
                style={{
                  width: '50%',
                  marginLeft: '25%',
                }}
                onClick={close}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        </Stack>
      </Box>

      {/* Modal for warning */}
      <AdminModal
        open={incompleteWarningModalOpen}
        onClose={() => setIncompleteWarningModalOpen(false)}>
        <Card sx={{ margin: 4, marginTop: 3 }}>
          <Box py={3} px={3} mt={3} mx={3}>
            <Stack spacing={2}>
              <Typography variant="h6" mb={1}>
                Warning
              </Typography>
              {incompleteFields.length > 0 && (
                <>
                  <Typography>
                    The following information is incomplete:
                  </Typography>
                  {incompleteFields.map((field, index) => (
                    <p style={{ color: 'red' }} key={index}>
                      {field}
                    </p>
                  ))}
                </>
              )}
              {passwordErrors.length > 0 && (
                <>
                  <Typography>Password validation errors:</Typography>
                  {passwordErrors.map((error, index) => (
                    <p style={{ color: 'red' }} key={`password-${index}`}>
                      {error}
                    </p>
                  ))}
                </>
              )}
              <Button
                variant="contained"
                color="primary"
                style={{
                  color: '#FFF',
                }}
                onClick={() => setIncompleteWarningModalOpen(false)}>
                Accept
              </Button>
            </Stack>
          </Box>
        </Card>
      </AdminModal>

      {/* Modal for confirm/results */}
      <AdminModal
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}>
        <Card
          sx={{
            margin: 2,
            marginTop: 2,
            maxWidth: 1000,
            maxHeight: '90vh',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}>
          <Box py={2} px={2} sx={{ flex: 1, overflow: 'auto' }}>
            {showResults ? renderResultsContent() : renderConfirmContent()}
          </Box>
        </Card>
      </AdminModal>
    </>
  );
};

AddNewUser.propTypes = propTypes;

export default AddNewUser;
